import { IModel, IAttribute, Collection, IViolation, IValidationContext } from '../base/annotation';
import { Activity } from './activity';
import { Metamodel } from '../base/model';
import { ParameterConfigUtils } from '../adapters/linkedservices/parameter-config.util';
import { ParameterConfig } from './parameter-config';
import { format } from '../../shared/utils/string.util';
import { ParameterValue } from '../base/parameter-value';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { LinkedService } from '../linkedservice/linkedservice';
import { GlobalVariables } from '../../constants/globals';
import { Copier } from '../base/copier';

interface IStoredProcedureActivityValidators {
    validateLinkedService: (
        dataset: StoredProcedureActivity,
        value?: LinkedService,
        violation?: IViolation,
        context?: IValidationContext
    ) => Promise<IViolation[] | undefined>;
}

export const storedProcedureActivityValidators: IStoredProcedureActivityValidators = {
    validateLinkedService: undefined
};

@Metamodel.model({
    name: 'SqlServerStoredProcedure',
    display: [
        format(shellStringResources.ModelName_ActivityTemplate, shellStringResources.StoredProcedure),
        format(shellStringResources.ModelName_ActivitiesTemplate, shellStringResources.StoredProcedure)
    ],
    referable: true
})
export class StoredProcedureActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly NAME: IAttribute;
    public static readonly LINKED_SERVICE_PARAMTERS: IAttribute;
    public static readonly STORED_PROCEDURE_PARAMETERS: IAttribute;
    public static readonly DATABASE: IAttribute;
    public static readonly ENFORCE_ONETIME_EXECUTION: IAttribute;

    @Metamodel.attribute({
        display: shellStringResources.StoredProcedureName,
        constraint: {
            validator: StoredProcedureActivity.validateStoredProcedureName
        },
        templatable: true,
        association: 'NAME'
    })
    public storedProcedureName?: string;

    @Metamodel.attribute({
        display: shellStringResources.StoredProcedureParameters,
        constraint: {
            validator: ParameterConfigUtils.createParameterConfigurationValidator(true)
        },
        accepts: ['Object', 'Array'],
        association: 'STORED_PROCEDURE_PARAMETERS'
    })
    public sprocParameters?: ParameterConfig[] = [];

    @Metamodel.attribute({
        display: !GlobalVariables.isTrident
            ? shellStringResources.ModelName_LinkedService
            : shellStringResources.ConnectionLabel,
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            required: true,
            validator: (...args) =>
                storedProcedureActivityValidators.validateLinkedService(
                    args[0] as StoredProcedureActivity,
                    ...args.slice(1)
                )
        }
    })
    public linkedService?: LinkedService;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public dmtsCredentialForArtifact?: LinkedService;

    public linkedServiceType?: string;

    /**
     * Parameter value for referenced linked service.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'LINKED_SERVICE_PARAMTERS'
    })
    public linkedServiceParameters: ParameterValue[] = [];

    @Metamodel.attribute({
        display: shellStringResources.Database,
        templatable: true,
        association: 'DATABASE'
    })
    public database?: string;

    @Metamodel.attribute({
        display: shellStringResources.EnforceOneTimeExecution,
        templatable: true,
        association: 'ENFORCE_ONETIME_EXECUTION'
    })
    public enforceOneTimeExecution?: boolean;

    /**
     * Addition properties for referenced linked service in fabric.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'LINKED_SERVICE_PROPERTIES'
    })
    public linkedServiceProperties: ParameterConfig[] = [];

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    public static validateStoredProcedureName(
        activity: StoredProcedureActivity,
        value: LinkedService,
        violation?: IViolation,
        context?: IValidationContext
    ): IViolation[] {
        if (
            StoredProcedureActivity.NAME.value(activity) == null &&
            StoredProcedureActivity.NAME.templateValue(activity) == null
        ) {
            return [
                {
                    context: activity,
                    message: () =>
                        format(shellStringResources.RequiredTemplate, shellStringResources.StoredProcedureName),
                    messageType: 'missing'
                }
            ];
        }
        return [];
    }

    public onActivityCloned(clonedActivity: StoredProcedureActivity, context: { inlineDataset?: boolean }): void {
        if (GlobalVariables.isTrident) {
            if (this.dmtsCredentialForArtifact?.parameterized || this.linkedService?.parameterized) {
                if (this.dmtsCredentialForArtifact != null) {
                    clonedActivity.dmtsCredentialForArtifact = Copier.clone(
                        context,
                        [this.dmtsCredentialForArtifact],
                        undefined,
                        true
                    )[0] as LinkedService;
                }
                if (this.linkedService != null) {
                    clonedActivity.linkedService = Copier.clone(
                        context,
                        [this.linkedService],
                        undefined,
                        true
                    )[0] as LinkedService;
                }
            }
        }
    }
}
